import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import authService from '../services/authService';
import { checkAuth } from '../store/authSlice';

// EL TEU COMPTE.
//
// Fins al 25/08/2026 no existia, i per canviar-se la contrasenya —sabent-la— calia passar per
// «no puc entrar», esperar que un admin generés un codi i fer-lo servir. Per a una contrasenya
// que ja tens és una volta absurda, i omplia la cua de l'administració de peticions que no eren
// cap problema.
//
// El correu no es pot canviar des d'aquí. És l'identificador amb què s'entra i el backend no ho
// accepta; ensenyar-lo com a camp editable seria oferir una cosa que no passarà.
//
// ── LA DISPOSICIÓ (27/08/2026) ──────────────────────────────────────────────────────────────
//
// Era una columna de 36 rem amb les dues targetes una sota l'altra, i **calia baixar per veure el
// botó de canviar la contrasenya** mentre dos terços de la pantalla eren buits a la dreta.
//
// Ara van de costat a partir de `lg`, i **les dues contrasenyes noves comparteixen fila**: són el
// mateix pas —escriu-la i repeteix-la—, i posades juntes es llegeix que van aparellades. Amb
// això la pàgina hi cap sencera i no cal desplaçar res.
//
// `items-start` no és decoració: sense això la targeta del nom s'estiraria fins a l'alçada de la
// de la contrasenya i quedaria mig buida.

const Compte = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [nom, setNom] = useState(user?.name || '');
  const [clau, setClau] = useState({ current: '', nova: '', repeticio: '' });
  const [feina, setFeina] = useState(null);

  const desaNom = async (e) => {
    e.preventDefault();
    setFeina('nom');
    try {
      await authService.updateProfile({ name: nom.trim() });
      await dispatch(checkAuth());     // perquè la barra de dalt digui el nom nou de seguida
      toast.success(t('account.saved'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('account.failed'));
    } finally { setFeina(null); }
  };

  const canviaClau = async (e) => {
    e.preventDefault();
    if (clau.nova.length < 8) return toast.error(t('auth.passwordTooShort'));
    if (clau.nova !== clau.repeticio) return toast.error(t('auth.passwordsDoNotMatch'));
    setFeina('clau');
    try {
      await authService.changePassword(clau.current, clau.nova);
      setClau({ current: '', nova: '', repeticio: '' });
      toast.success(t('account.changed'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('account.failed'));
    } finally { setFeina(null); }
  };

  const camp = (etiqueta, props) => (
    <div>
      <label htmlFor={props.id} className="block text-sm font-medium text-gray-700 mb-1">
        {etiqueta}
      </label>
      <input
        className="w-full px-3.5 py-2.5 border border-gray-300 rounded-md text-[15px]
                   focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent"
        {...props} />
    </div>
  );

  const boto = (text, ocupat) => (
    <button type="submit" disabled={ocupat}
      className="px-5 py-2.5 bg-primary-700 text-white rounded-md text-sm font-semibold
                 hover:bg-primary-800 disabled:opacity-60">
      {text}
    </button>
  );

  return (
    <div className="max-w-5xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('account.title')}</h1>
      <p className="text-gray-600 mb-8">
        {t('account.who')} <b className="text-gray-900">{user?.email}</b>
        {' · '}{t('account.role')}: {user?.role ? t(`roles.${user.role}`) : ''}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <form onSubmit={desaNom}
            className="bg-white border border-gray-200 rounded-md p-6 space-y-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">{t('account.nameSection')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('account.nameHint')}</p>
        </div>
        {camp(t('account.name'), { id: 'c-nom', type: 'text', required: true, maxLength: 255,
          value: nom, onChange: (e) => setNom(e.target.value) })}
        {boto(feina === 'nom' ? t('account.saving') : t('account.save'), feina === 'nom')}
      </form>

      <form onSubmit={canviaClau}
            className="bg-white border border-gray-200 rounded-md p-6 space-y-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">{t('account.passwordSection')}</h2>
          <p className="text-sm text-gray-500 mt-1">{t('account.passwordHint')}</p>
        </div>
        {camp(t('account.current'), { id: 'c-actual', type: 'password', required: true,
          autoComplete: 'current-password',
          value: clau.current, onChange: (e) => setClau({ ...clau, current: e.target.value }) })}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {camp(t('account.new'), { id: 'c-nova', type: 'password', required: true, minLength: 8,
            autoComplete: 'new-password',
            value: clau.nova, onChange: (e) => setClau({ ...clau, nova: e.target.value }) })}
          {camp(t('account.repeat'), { id: 'c-rep', type: 'password', required: true,
            autoComplete: 'new-password',
            value: clau.repeticio, onChange: (e) => setClau({ ...clau, repeticio: e.target.value }) })}
        </div>
        {boto(feina === 'clau' ? t('account.changing') : t('account.change'), feina === 'clau')}
      </form>
      </div>
    </div>
  );
};

export default Compte;
